import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { message, Tooltip } from "antd";
import { resetPassword } from "../../api";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { LANGUAGE } from "services/general";
import { changeLanguage } from "utils";
import LoginBrandLogo from "modules/auth/components/login/LoginBrandLogo";

const ResetPassword = ({ data }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const isDark = +localStorage.getItem("isDark");

  const handleModeClick = () => {
    localStorage.setItem("isDark", isDark ? 0 : 1);
    window.location.reload();
  };

  const changeLng = () =>
    changeLanguage(i18n, i18n.language === "ar" ? "en" : "ar");

  const handleSubmit = async (ev) => {
    ev.preventDefault();

    const formData = new FormData(ev.target);
    const password = formData.get("password");
    const password_confirmation = formData.get("password_confirmation");

    if (password !== password_confirmation) {
      setPasswordError(t("passwords_not_match"));
      return;
    }

    setPasswordError("");

    try {
      const res = await resetPassword({
        password,
        password_confirmation,
        ...data,
      });
      if (res.status) {
        navigate(`/${LANGUAGE}/login`);
      } else {
        message.error(t(res.data) || res.message || t("somethingWentWrong"));
      }
    } catch (error) {
      message.error(t("somethingWentWrong"));
    }
  };

  return (
    <section className="flex flex-col lg:flex-row items-stretch relative min-h-screen h-auto lg:h-screen">
      <div className="absolute top-6 sm:top-8 z-[60] end-5 sm:end-6 lg:end-[calc(48%+20px)] inline-flex items-center gap-3">
        <div
          className={`
            w-9 h-9 p-1 rounded-[20px] border
            inline-flex shrink-0 justify-center items-center cursor-pointer
            ${isDark ? "bg-surface/10 border-border-light/20" : "bg-surface border-border-light"}
          `}
          onClick={handleModeClick}
        >
          <img
            src={isDark ? "/icons/sun.svg" : "/icons/moon.svg"}
            alt="toggle mode"
          />
        </div>
        <Tooltip title={i18n.language === "ar" ? "English" : "عربي"}>
          <div
            className={`
            w-9 h-9 p-1 rounded-[20px] border
            inline-flex shrink-0 justify-center items-center cursor-pointer
            ${isDark ? "bg-surface/10 border-border-light/20" : "bg-surface border-border-light"}
          `}
            onClick={changeLng}
            title={t("login.toggle_language_title")}
          >
            <img
              src={
                isDark ? "/icons/mlti-lang-dark.svg" : "/icons/mlti-lang.svg"
              }
              alt="toggle language"
              className="w-5 h-5"
            />
          </div>
        </Tooltip>
      </div>

      <LoginBrandLogo isDark={isDark} />
      <div
        className={`w-full lg:w-[52%] flex flex-col justify-center px-4 sm:px-8 lg:px-[72px] py-8 lg:py-0 lg:h-full pb-[70px] ${
          isDark ? "bg-invoice-dark" : "bg-surface"
        }`}
      >
        <h1
          className={`text-xl sm:text-2xl lg:text-[24px] font-bold mt-6 mb-2.5 leading-6 sm:leading-8 ${
            isDark ? "login-text-color" : "text-invoice-blue"
          }`}
        >
          {t("login.reset")}
        </h1>

        <span
          className={`text-sm inline-block ${
            isDark ? "text-text-subtle" : "text-secondary"
          }`}
        >
          {t("login.input")}
        </span>

        <form
          className="w-full max-w-md lg:max-w-lg mt-8"
          onSubmit={handleSubmit}
        >
          <div className="relative mt-4">
            <input
              id="password-field"
              type={showPassword ? "text" : "password"}
              placeholder={t("login.login_password_holder")}
              className={`w-full h-[56px] rounded-lg border ps-14 pe-6 text-[16px] focus:outline-none bg-transparent ${
                isDark
                  ? "border-border-light/60 text-surface/90 placeholder:text-text-subtle/70"
                  : "border-border-light text-text-strong placeholder:text-text-muted"
              }`}
              required
              minLength={6}
              name="password"
            />
            <button
              type="button"
              className={`absolute start-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-md ${
                isDark ? "text-text-subtle" : "text-secondary"
              }`}
              onClick={() => setShowPassword((prev) => !prev)}
            >
              {showPassword ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
            </button>
          </div>

          <div className="relative mt-4">
            <input
              id="password-field2"
              type={showConfirmPassword ? "text" : "password"}
              placeholder={t("login.confirm_password")}
              className={`w-full h-[56px] rounded-lg border ps-14 pe-6 text-[16px] focus:outline-none bg-transparent ${
                isDark
                  ? "border-border-light/60 text-surface/90 placeholder:text-text-subtle/70"
                  : "border-border-light text-text-strong placeholder:text-text-muted"
              } ${passwordError ? "border-danger" : ""}`}
              required
              minLength={6}
              name="password_confirmation"
              onChange={() => setPasswordError("")}
            />
            <button
              type="button"
              className={`absolute start-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-md ${
                isDark ? "text-text-subtle" : "text-secondary"
              }`}
              onClick={() => setShowConfirmPassword((prev) => !prev)}
            >
              {showConfirmPassword ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
            </button>
          </div>

          {passwordError && (
            <p className="text-danger text-sm mt-1.5 ps-1">{passwordError}</p>
          )}
          <button
            type="submit"
            className="bg-invoice-blue hover:bg-invoice-blue/90 rounded-lg flex items-center justify-center w-full h-[48px] sm:h-[56px] text-surface font-semibold mt-8 text-sm sm:text-base duration-200"
          >
            {t("login.reset")}
          </button>
          <Link
            to={`/${LANGUAGE}/login`}
            className={`rounded-lg flex items-center justify-center w-full h-[48px] sm:h-[56px] font-semibold mt-[14px] text-sm sm:text-base border duration-200 ${
              isDark
                ? "bg-transparent hover:bg-surface/10 text-text-subtle border-border-light/30"
                : "bg-transparent hover:bg-surface-muted text-secondary border-border-light"
            }`}
          >
            {t("login.back_login")}
          </Link>
        </form>
      </div>

      <div
        className="relative w-full lg:w-[48%] min-h-[400px] lg:h-full bg-no-repeat bg-center order-first lg:order-last login-bg-image"
        style={{
          backgroundImage: "url('/login-img.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="lg:hidden absolute inset-0 z-50 flex items-center justify-center"></div>
      </div>
    </section>
  );
};

export default ResetPassword;
